import { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as ProgressIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { GeneralTableBox, HeadTableCell, RowTableCell } from '../../../../common/components';
import { formatDate as formatDateShared } from '../../../../utils/formatUtils';

const formatProjectDate = (dateString) => formatDateShared(dateString, { fallback: 'No definida' });

/**
 * Tabla de datos de proyectos siguiendo los patrones del proyecto
 */
const ProjectsDataTable = ({
  data = [],
  isLoading = false,
  onEdit,
  onDelete,
  onStatusChange,
  onConstructionTypeChange,
  onViewProgress,
  onViewQuality,
  onAssignTemplates,
  LoadingComponent = 'div',
  subscribers = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [constructionTypeMenuAnchor, setConstructionTypeMenuAnchor] = useState(null);
  const [selectedStatusProject, setSelectedStatusProject] = useState(null);
  const [selectedConstructionProject, setSelectedConstructionProject] = useState(null);

  const handleOpenMenu = (event, project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleOpenStatusMenu = (event, project) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedStatusProject(project);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setSelectedStatusProject(null);
  };

  const handleOpenConstructionMenu = (event, project) => {
    setConstructionTypeMenuAnchor(event.currentTarget);
    setSelectedConstructionProject(project);
  };

  const handleCloseConstructionMenu = () => {
    setConstructionTypeMenuAnchor(null);
    setSelectedConstructionProject(null);
  };

  const handleStatusChange = (newStatus) => {
    if (selectedStatusProject && onStatusChange) {
      onStatusChange(selectedStatusProject, newStatus);
    }
    handleCloseStatusMenu();
  };

  const handleConstructionTypeChange = (newType) => {
    if (selectedConstructionProject && onConstructionTypeChange) {
      onConstructionTypeChange(selectedConstructionProject, newType);
    }
    handleCloseConstructionMenu();
  };

  const handleMenuAction = (action, ...args) => {
    action(...args);
    handleCloseMenu();
  };

  const renderSubscribers = (project) => {
    let projectSubscribers = [];

    if (project.contract?.subscribers && project.contract.subscribers.length > 0) {
      projectSubscribers = project.contract.subscribers;
    } else if (project.subscribers && project.subscribers.length > 0) {
      if (typeof project.subscribers[0] === 'object') {
        projectSubscribers = project.subscribers;
      } else {
        // IDs lookup
        projectSubscribers = subscribers.filter((s) => project.subscribers.includes(s.uuid));
      }
    }

    if (projectSubscribers.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Sin asignar
        </Typography>
      );
    }

    const firstSubscriber = projectSubscribers[0];
    const firstSubscriberName = `${firstSubscriber.first_name} ${firstSubscriber.last_name}`;

    if (projectSubscribers.length === 1) {
      return <Typography variant="body2">{firstSubscriberName}</Typography>;
    }

    const extraCount = projectSubscribers.length - 1;
    const allNames = projectSubscribers.map((s) => `${s.first_name} ${s.last_name}`).join('\n');

    return (
      <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{allNames}</div>} arrow>
        <Typography
          variant="body2"
          sx={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}
        >
          {firstSubscriberName}
          <Box
            component="span"
            sx={{
              ml: 1,
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: '12px',
              px: 0.8,
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}
          >
            +{extraCount}
          </Box>
        </Typography>
      </Tooltip>
    );
  };

  const statusConfig = {
    not_started: { label: 'Sin empezar', color: 'default' },
    in_progress: { label: 'En progreso', color: 'info' },
    completed: { label: 'Completado', color: 'success' },
    cancelled: { label: 'Cancelado', color: 'error' },
  };

  const constructionTypeConfig = {
    not_assigned: {
      label: 'No asignado',
      sx: {
        color: '#424242',
        borderColor: '#424242',
        backgroundColor: 'rgba(66, 66, 66, 0.08)',
      },
    },
    obra_gruesa: {
      label: 'Obra Gruesa',
      sx: {
        color: '#5D4037',
        borderColor: '#5D4037',
        backgroundColor: 'rgba(93, 64, 55, 0.08)',
      },
    },
    obra_fina: {
      label: 'Obra Fina',
      sx: {
        color: '#8D6E63',
        borderColor: '#8D6E63',
        backgroundColor: 'rgba(141, 110, 99, 0.08)',
      },
    },
  };

  const headers = [
    { label: 'Nombre', key: 'name' },
    { label: 'Suscriptor', key: 'subscriber', hideOnMobile: true },
    { label: 'Estado', key: 'status' },
    { label: 'Tipo de Obra', key: 'construction_type', hideOnMobile: true },
    { label: 'Fecha Inicio', key: 'start_date', hideOnMobile: true },
    { label: 'Fecha Fin', key: 'end_date', hideOnMobile: true },
    { label: 'Acciones', key: 'actions' },
  ];

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <>
      <GeneralTableBox>
        <Table aria-label="projects table">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <HeadTableCell
                  key={header.key}
                  sx={{
                    display: header.hideOnMobile ? { xs: 'none', md: 'table-cell' } : 'table-cell',
                  }}
                >
                  {header.label}
                </HeadTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((project) => {
              const status = statusConfig[project.status] || statusConfig.not_started;
              const constructionType =
                constructionTypeConfig[project.construction_type] ||
                constructionTypeConfig.not_assigned; // Default a no asignado
              return (
                <TableRow key={project.uuid}>
                  <RowTableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {project.name}
                      </Typography>
                      {project.description && (
                        <Typography variant="caption" color="text.secondary">
                          {project.description.substring(0, 50)}
                          {project.description.length > 50 ? '...' : ''}
                        </Typography>
                      )}
                    </Box>
                  </RowTableCell>

                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {renderSubscribers(project)}
                  </RowTableCell>

                  <RowTableCell>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleOpenStatusMenu(e, project)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                  </RowTableCell>

                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                      label={constructionType.label}
                      size="small"
                      variant="outlined"
                      onClick={(e) => handleOpenConstructionMenu(e, project)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        ...constructionType.sx,
                      }}
                    />
                  </RowTableCell>

                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2">{formatProjectDate(project.start_date)}</Typography>
                  </RowTableCell>

                  <RowTableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2">{formatProjectDate(project.end_date)}</Typography>
                  </RowTableCell>

                  <RowTableCell
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}
                  >
                    <Tooltip title="Ver progreso de obra">
                      <IconButton
                        size="small"
                        onClick={() => onViewProgress(project)}
                        color="primary"
                      >
                        <ProgressIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Editar proyecto">
                      <IconButton size="small" onClick={() => onEdit(project)} color="default">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Eliminar proyecto">
                      <IconButton size="small" onClick={() => onDelete(project)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/* Solo el menú para cambiar estado si el proyecto no ha empezado */}
                    {project.status === 'not_started' && (
                      <IconButton size="small" onClick={(e) => handleOpenMenu(e, project)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </RowTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </GeneralTableBox>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedProject?.status === 'not_started' && (
          <MenuItem
            onClick={() => handleMenuAction(onStatusChange, selectedProject, 'in_progress')}
          >
            <StartIcon fontSize="small" sx={{ mr: 1 }} />
            Iniciar Proyecto
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => handleStatusChange('not_started')}>
          <Chip
            label="Sin empezar"
            color="default"
            size="small"
            variant="outlined"
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('in_progress')}>
          <Chip
            label="En progreso"
            color="info"
            size="small"
            variant="outlined"
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('completed')}>
          <Chip
            label="Completado"
            color="success"
            size="small"
            variant="outlined"
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('cancelled')}>
          <Chip
            label="Cancelado"
            color="error"
            size="small"
            variant="outlined"
            sx={{ pointerEvents: 'none' }}
          />
        </MenuItem>
      </Menu>

      {/* Menú para cambiar tipo de construcción */}
      <Menu
        anchorEl={constructionTypeMenuAnchor}
        open={Boolean(constructionTypeMenuAnchor)}
        onClose={handleCloseConstructionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => handleConstructionTypeChange('not_assigned')}>
          <Chip
            label="No asignado"
            size="small"
            variant="outlined"
            sx={{
              pointerEvents: 'none',
              color: '#424242',
              borderColor: '#424242',
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleConstructionTypeChange('obra_gruesa')}>
          <Chip
            label="Obra Gruesa"
            size="small"
            variant="outlined"
            sx={{
              pointerEvents: 'none',
              color: '#5D4037',
              borderColor: '#5D4037',
              backgroundColor: 'rgba(93, 64, 55, 0.08)',
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleConstructionTypeChange('obra_fina')}>
          <Chip
            label="Obra Fina"
            size="small"
            variant="outlined"
            sx={{
              pointerEvents: 'none',
              color: '#8D6E63',
              borderColor: '#8D6E63',
              backgroundColor: 'rgba(141, 110, 99, 0.08)',
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProjectsDataTable;
